/**
 * The files attached to one thing, and a way to attach another.
 *
 * Upload and attach are two calls on purpose: the same bytes uploaded twice are
 * one stored document, and one document may support several things (the same
 * quotation PDF behind a request and its exception). Files are voided, never
 * deleted, so the history of why a file was removed stays.
 */

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../api'
import { useAuth } from '../auth'
import { ErrorNote } from './ui'

export interface Attached {
  id: string
  link_id: string
  original_filename: string
  mime_type: string
  size_bytes: number
  uploaded_by_name: string | null
  uploaded_at: string
  is_void: boolean
  void_reason: string | null
  kind: string
}

async function upload(file: File): Promise<{ id: string }> {
  const body = new FormData()
  body.append('file', file)
  const response = await fetch('/api/v1/documents', {
    method: 'POST',
    body,
    credentials: 'include',
  })
  const data = await response.json().catch(() => null)
  if (!response.ok) {
    const detail = data?.detail
    throw new ApiError(
      response.status,
      typeof detail === 'string' ? detail : `upload failed (${response.status})`,
      detail,
    )
  }
  return data
}

export default function Attachments({
  subjectType,
  subjectId,
  kind,
  canAttach = true,
  label = 'Attach a file',
}: {
  subjectType: string
  subjectId: string
  kind: string
  canAttach?: boolean
  label?: string
}) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const key = ['attachments', subjectType, subjectId]

  const files = useQuery({
    queryKey: key,
    queryFn: () =>
      api.get<Attached[]>(
        `/documents?subject_type=${encodeURIComponent(subjectType)}&subject_id=${subjectId}`,
      ),
    enabled: can('doc.attachment.view_own'),
  })

  const attach = useMutation({
    mutationFn: async (file: File) => {
      setBusy(true)
      try {
        const stored = await upload(file)
        await api.post(`/documents/${stored.id}/links`, {
          subject_type: subjectType,
          subject_id: subjectId,
          kind,
        })
      } finally {
        setBusy(false)
      }
    },
    onSuccess: () => {
      if (input.current) input.current.value = ''
      void queryClient.invalidateQueries({ queryKey: key })
      // Counts shown elsewhere (quotations "with document") depend on this.
      void queryClient.invalidateQueries({ queryKey: ['quotations'] })
      void queryClient.invalidateQueries({ queryKey: ['quotation-requirement'] })
      // A bank-paid bill's invoice decides whether its credit can be claimed,
      // which the Banking, Bills and GST screens all show.
      if (subjectType === 'vendor_invoice') {
        for (const shown of ['bank-txns', 'invoices', 'itc', 'gstr3b']) {
          void queryClient.invalidateQueries({ queryKey: [shown] })
        }
      }
    },
  })

  const live = (files.data ?? []).filter((f) => !f.is_void)

  return (
    <div className="attachments">
      <ErrorNote error={files.error ?? attach.error} />
      {live.length === 0 ? (
        <span className="faint">No file attached.</span>
      ) : (
        <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>
          {live.map((f) => (
            <li key={f.link_id}>
              <a href={`/api/v1/documents/${f.id}/content`} target="_blank" rel="noreferrer">
                {f.original_filename}
              </a>{' '}
              <span className="faint">
                {Math.max(1, Math.round(f.size_bytes / 1024))} KB
                {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {canAttach && can('doc.attachment.create') && (
        <div style={{ marginTop: 4 }}>
          <input
            ref={input}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) attach.mutate(file)
            }}
          />
          <button
            type="button"
            className="small"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? 'Uploading…' : label}
          </button>
        </div>
      )}
    </div>
  )
}
